import { useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchBillingPlansThunk,
  selectBillingState,
  setBillingCycle,
  updateBillingPlanThunk,
} from "../store/billingSlice";

const toNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

export const useBillingPlans = () => {
  const dispatch = useDispatch();
  const state = useSelector(selectBillingState);

  useEffect(() => {
    if (state.hasPlansLoaded) return;
    dispatch(fetchBillingPlansThunk());
  }, [dispatch, state.hasPlansLoaded]);

  const getDisplayedPrice = useCallback(
    (plan) =>
      state.billingCycle === "annual"
        ? toNumber(plan?.annualPrice, toNumber(plan?.price, 0))
        : toNumber(plan?.price, 0),
    [state.billingCycle],
  );

  const plansData = useMemo(
    () =>
      state.plans.map((plan) => ({
        ...plan,
        current: Number(plan.id) === Number(state.currentPlanId),
      })),
    [state.currentPlanId, state.plans],
  );

  const currentPlan = useMemo(
    () => plansData.find((plan) => plan.current),
    [plansData],
  );

  const currentPrice = useMemo(
    () => getDisplayedPrice(currentPlan || {}),
    [currentPlan, getDisplayedPrice],
  );

  const getButtonText = useCallback(
    (plan) => {
      if (plan.current) return plan.cta;
      if (plan.isCustom || plan.id === "custom") return plan.cta;

      const planPrice = getDisplayedPrice(plan);
      if (!planPrice) return plan.cta;

      return planPrice < currentPrice ? "DOWNGRADE PLAN" : "UPGRADE PLAN";
    },
    [currentPrice, getDisplayedPrice],
  );

  const handleChangePlan = useCallback(
    async (plan) => {
      const action = await dispatch(updateBillingPlanThunk(plan));
      if (updateBillingPlanThunk.fulfilled.match(action)) {
        return {
          success: true,
          message: `Plan updated to ${action.payload.planName}.`,
        };
      }

      return {
        success: false,
        message: action.payload || "Unable to update plan.",
      };
    },
    [dispatch],
  );

  return {
    billingCycle: state.billingCycle,
    isLoadingPlans: state.isLoadingPlans,
    isUpdatingPlanId: state.isUpdatingPlanId,
    plansError: state.plansError,
    plansData,
    currentPlan,
    currentClient: state.currentClient,
    getDisplayedPrice,
    getButtonText,
    setBillingCycle: (value) => dispatch(setBillingCycle(value)),
    handleChangePlan,
  };
};

export default useBillingPlans;
